import type { SeverityLevel } from '@sentry/react';
import { captureException as sentryCaptureException } from '@sentry/react';
import type { QueryClientConfig } from '@tanstack/react-query';
import { QueryCache, QueryClient } from '@tanstack/react-query';

import { makeToast } from '@cognite/cogs-lab-v11';
import type { CogsToastProps } from '@cognite/cogs-lab-v11';

type ToastOptions = NonNullable<Parameters<typeof makeToast>[1]>;

/* This type will be automatically supported by react query in v5 using module augmentation and then we won't need to assert type in query
https://tanstack.com/query/v5/docs/framework/react/typescript#typing-meta
*/
export type QueryMeta = {
  error?: {
    message?: string;
    name?: string;
    /* Sentry severity for captureError — defaults to 'error' to match prior
    hardcoded behavior. Set explicitly when a query historically reported at
    'fatal'/'warning' etc. */
    level?: SeverityLevel;
    captureError?: boolean;
    toast?:
      | boolean
      | {
          body?: string;
          type?: CogsToastProps['type'];
          autoClose?: ToastOptions['autoClose'];
        };
  };
  metrics?: {
    kind: Array<'sli' | 'analytics'>;
    name?: string;
  };
};

export const isQueryMeta = (meta: unknown): meta is QueryMeta => {
  return typeof meta === 'object' && meta !== null;
};

/* Kept generic on purpose — this file has no relative imports so it can be
copy-pasted into a shared library as-is. Each app's own status enum (if any)
maps to this at the call site, not in here. */
export type QueryOutcome = 'success' | 'error';

export type InstrumentedQueryClientDeps = {
  /* Required — there's no generic default: every app has its own namespace/app
  name baked into how it reports SLI/analytics metrics. */
  trackSLI: (name: string, elapsedMs: number, status: QueryOutcome) => void;
  trackEvent: (name: string, properties?: Record<string, unknown>) => void;
  /* Optional — both already resolve to the single shared implementation every
  app in this monorepo uses, so a sensible default is safe here. */
  captureException?: typeof sentryCaptureException;
  showToast?: (options: CogsToastProps, toastOptions?: ToastOptions) => void;
};

export type QueryClientOverrides = {
  queries?: NonNullable<QueryClientConfig['defaultOptions']>['queries'];
  mutations?: NonNullable<QueryClientConfig['defaultOptions']>['mutations'];
};

const exponentialBackoff = (attempts: number) =>
  Math.min(1000 * 2 ** attempts, 30000);

export const createInstrumentedQueryClient =
  (deps: InstrumentedQueryClientDeps) =>
  (overrides?: QueryClientOverrides): QueryClient => {
    const {
      trackSLI,
      trackEvent,
      captureException = sentryCaptureException,
      showToast = makeToast,
    } = deps;
    const startTimes = new Map<string, number>();

    const queryCache = new QueryCache({
      onError(error, query) {
        const meta = query.meta;
        const isMeta = isQueryMeta(meta);
        const queryKey =
          typeof query.queryKey[0] === 'string' ? query.queryKey[0] : '';

        if (isMeta) {
          const metricName = meta?.metrics?.name ?? queryKey;
          if (!metricName) return;

          const tag = meta.error?.name ?? metricName;
          const trackSli = meta.metrics?.kind?.includes('sli');
          const analytics = meta.metrics?.kind?.includes('analytics');
          const startTime = startTimes.get(query.queryHash);

          if (startTime !== undefined) {
            const elapsed = Date.now() - startTime;
            if (trackSli) {
              trackSLI(metricName, elapsed, 'error');
            } else if (analytics) {
              trackEvent(metricName, {
                sliTimerMilliseconds: elapsed,
                status: 'error',
              });
            }
            startTimes.delete(query.queryHash);
          }

          if (meta?.error?.captureError) {
            captureException(error, {
              level: meta.error.level ?? 'error',
              tags: { queryKey: tag },
            });
          }

          if (meta?.error?.toast) {
            const toastOptions =
              typeof meta.error.toast === 'object' ? meta.error.toast : {};
            const toastBody = {
              body:
                toastOptions.body ??
                meta.error?.message ??
                (error instanceof Error
                  ? error.message
                  : 'Something went wrong'),
              type: toastOptions.type ?? 'danger',
            };
            showToast(toastBody, {
              autoClose: toastOptions.autoClose ?? false,
            });
          }
        }
      },
      onSuccess(_data, query) {
        const isMeta = isQueryMeta(query.meta);
        if (!isMeta) return;
        const meta = query.meta;
        const queryKey =
          typeof query.queryKey[0] === 'string' ? query.queryKey[0] : undefined;
        const metricName = meta?.metrics?.name ?? queryKey;
        if (!metricName) return;

        const trackSli = meta?.metrics?.kind?.includes('sli');
        const analytics = meta?.metrics?.kind?.includes('analytics');
        const startTime = startTimes.get(query.queryHash);
        if (startTime !== undefined) {
          const elapsed = Date.now() - startTime;
          if (trackSli) {
            trackSLI(metricName, elapsed, 'success');
          } else if (analytics) {
            trackEvent(metricName, {
              sliTimerMilliseconds: elapsed,
              status: 'success',
            });
          }
          startTimes.delete(query.queryHash);
        }
      },
    });

    queryCache.subscribe((event) => {
      if (event.type !== 'updated') return;
      const { query, action } = event;
      const isMeta = isQueryMeta(query.meta);
      if (!isMeta) return;
      const meta = query.meta;
      const queryKey =
        typeof query.queryKey[0] === 'string' ? query.queryKey[0] : undefined;
      const metricName = meta?.metrics?.name ?? queryKey;
      if (!metricName) return;
      if (action.type === 'fetch') {
        startTimes.set(query.queryHash, Date.now());
      }
    });

    return new QueryClient({
      queryCache,
      defaultOptions: {
        ...overrides,
        queries: {
          refetchOnReconnect: false,
          refetchOnWindowFocus: false,
          retry: 3,
          retryDelay: exponentialBackoff,
          ...overrides?.queries,
        },
      },
    });
  };
